import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bot, X, Send, Compass, ArrowRight, Zap, MessageCircle, FileText, DollarSign, Info, Mail, BookOpen, ChevronDown, GripVertical, Calendar, Paperclip, Download, ArrowLeft, Check, CheckCheck, ExternalLink, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import HaliLeadsAgent from './hali-leads-agent/HaliLeadsAgent';
import { getHalitradeUrl } from '../config/landingLinks';

// Platform logos mapping
const PLATFORM_LOGOS = {
  halitrade: { light: '/assets/halitrade-logo-text.svg', dark: '/assets/halitrade-logo-text-dark.svg' },
  haliagent: { light: '/assets/haliagent-logo-yellow.svg', dark: '/assets/haliagent-logo-yellow-dark.svg' },
  halilab: { light: '/assets/halilab-logo-text.svg', dark: '/assets/halilab-logo-text-dark.svg' },
  halisoft: { light: '/assets/halisoft-logo.svg', dark: '/assets/halisoft-logo-dark.svg' }
};

// Get platform from URL
const getPlatformFromUrl = (url) => {
  if (url.includes('halitrade')) return 'halitrade';
  if (url.includes('haliagent')) return 'haliagent';
  if (url.includes('halilab')) return 'halilab';
  if (url.includes('contact') || url.includes('products')) return 'halisoft';
  return null;
};

const BADGE_ID = 'haliguide-floating-badge';

/**
 * Landing Page Guide Agent
 * Intelligent AI assistant that helps visitors find the right platform and next steps
 * Uses Claude AI to understand user needs and provide personalized guidance
 */
const LandingGuideAgent = ({ darkMode, theme = 'default', position = 'right' }) => {
  // Skip rendering entirely during react-snap pre-rendering
  const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');
  const isValidInstance = !isPrerendering;
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const goToPath = useCallback((path) => {
    setIsOpen(false);
    window.location.assign(path);
  }, []);

  // Debug: log badge mount status
  useEffect(() => {
    if (isValidInstance) {
      // console.log('[HaliGuide] Badge mounted - theme:', theme);
    }
  }, [isValidInstance, theme]);

  // Helper to render message text with clickable markdown links and platform buttons
  const renderMessageWithLinks = (text) => {
    if (!text) return null;

    // Split text by markdown links [text](url)
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkPattern.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }

      // Add the link
      const linkText = match[1];
      const linkUrl = match[2];
      const platform = getPlatformFromUrl(linkUrl);

      // Check if this is a CTA link (starts with emoji like 👉)
      const isCTA = linkText.includes('Get Started') || linkText.includes('Schedule') || linkText.startsWith('👉');

      parts.push({
        type: 'link',
        text: linkText.replace('👉 ', ''),
        url: linkUrl,
        platform,
        isCTA
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }

    return parts.map((part, idx) => {
      if (part.type === 'text') {
        return <span key={idx}>{part.content}</span>;
      }

      if (part.type === 'link') {
        const logo = part.platform && PLATFORM_LOGOS[part.platform];

        // CTA button style - full width block
        if (part.isCTA) {
          return (
            <div key={idx} className="block w-full mt-3">
              <button
                onClick={() => {
                  if (/^https?:\/\//i.test(part.url)) {
                    window.location.assign(part.url);
                    return;
                  }
                  navigate(part.url);
                }}
                className={`w-full flex items-center justify-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all ${
                  part.platform === 'halitrade'
                    ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white'
                    : part.platform === 'haliagent'
                    ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-gray-900'
                    : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white'
                } shadow-lg hover:shadow-xl transform hover:scale-[1.02]`}
              >
                {logo && (
                  <img
                    src={darkMode ? logo.dark : logo.light}
                    alt={part.platform}
                    className="h-6"
                  />
                )}
                <span className="text-base">{part.text}</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          );
        }

        // Inline link with small logo
        return (
          <button
            key={idx}
            onClick={() => {
              if (/^https?:\/\//i.test(part.url)) {
                window.location.assign(part.url);
                return;
              }
              navigate(part.url);
            }}
            className={`inline-flex items-center gap-1 px-2 py-0.5 mx-0.5 rounded font-medium transition-colors ${
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-blue-300'
                : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
            }`}
          >
            {logo && (
              <img
                src={darkMode ? logo.dark : logo.light}
                alt={part.platform}
                className="h-3"
              />
            )}
            <span className="underline">{part.text}</span>
          </button>
        );
      }

      return null;
    });
  };

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [isCentered, setIsCentered] = useState(false); // Center panel when opened via Get Started
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showQuickLinks, setShowQuickLinks] = useState(true);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const messagesEndRef = useRef(null);
  const [badgePosition, setBadgePosition] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return null; // No saved position on mobile
    const saved = localStorage.getItem('haliGuideBadgePosition');
    if (!saved) return null;
    try {
      const pos = JSON.parse(saved);
      // Validate position fits within current viewport
      if (pos.x > window.innerWidth - 40 || pos.y > window.innerHeight - 40) return null;
      return pos;
    } catch { return null; }
  });
  const [badgeDrag, setBadgeDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0, startX: 0, startY: 0 });
  const badgeSuppressClickRef = useRef(false);

  // Cleanup handled by singleton useEffect above

  // View state: 'guide', 'aiChat', 'haliSales', 'liveChat', 'email', 'calendly'
  const [currentView, setCurrentView] = useState('guide');

  // AI Chat state (for AI Support Chat mode)
  const [aiChatMessages, setAiChatMessages] = useState([
    {
      type: 'bot',
      text: "👋 Hello! I'm your HaliSoft AI-powered support assistant. \n\nWe provide:\n• Invoice Factoring & Dynamic Discounting\n• Supply Chain Financing & Payables Programs\n• White-label SaaS for Financial Institutions\n• Trade Finance Consulting for Customers & Financial Entities\n\n🤖 Everything is done 100% online through our AI-powered platform - no email submissions!\n\nHow can I help you today?",
      timestamp: new Date()
    }
  ]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatIsTyping, setAiChatIsTyping] = useState(false);
  const [aiChatConversationId, setAiChatConversationId] = useState(null);
  const [isAIOnline, setIsAIOnline] = useState(true);
  const aiChatMessagesEndRef = useRef(null);

  // === LIVE CHAT STATE (embedded Contact Center) ===
  const [liveChatMessages, setLiveChatMessages] = useState([]);
  const [liveChatInput, setLiveChatInput] = useState('');
  const [liveChatSessionId, setLiveChatSessionId] = useState(() => {
    return localStorage.getItem('liveChatSessionId') || null;
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [lastReadMessageId, setLastReadMessageId] = useState(() => {
    const saved = localStorage.getItem('liveChatLastReadMessageId');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [newMessageAlert, setNewMessageAlert] = useState(false);
  const fileInputRef = useRef(null);
  const liveChatMessagesEndRef = useRef(null);

  // === EMAIL FORM STATE ===
  const [emailFormData, setEmailFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Helper to check if current session is a guest session
  const isGuestSession = (sessionId) => sessionId && sessionId.startsWith('chat_guest_');

  // Resizable panel state
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('haliGuidePanelWidth');
    return saved ? parseInt(saved, 10) : 384; // Default md:w-96 = 384px
  });
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState(() => {
    const saved = localStorage.getItem('haliGuidePanelPosition');
    return saved ? JSON.parse(saved) : null;
  });
  const [panelDrag, setPanelDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });

  // Handle panel resize
  const startResizing = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback((e) => {
    if (isResizing && panelRef.current) {
      let newWidth;
      if (position === 'right') {
        // For right-positioned panels, calculate from the right edge
        newWidth = window.innerWidth - e.clientX;
      } else {
        // For left-positioned panels
        newWidth = e.clientX - panelRef.current.getBoundingClientRect().left;
      }
      if (newWidth >= 320 && newWidth <= 600) {
        setPanelWidth(newWidth);
        localStorage.setItem('haliGuidePanelWidth', newWidth.toString());
      }
    }
  }, [isResizing, position]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
      document.body.style.userSelect = '';
    };
  }, [isResizing, resize, stopResizing]);

  const handleBadgePointerMove = useCallback((event) => {
    if (!badgeDrag.isDragging) return;
    const nextX = event.clientX - badgeDrag.offsetX;
    const nextY = event.clientY - badgeDrag.offsetY;
    if (
      Math.abs(event.clientX - badgeDrag.startX) > 3 ||
      Math.abs(event.clientY - badgeDrag.startY) > 3
    ) {
      badgeSuppressClickRef.current = true;
    }
    const maxX = Math.max(8, window.innerWidth - 168);
    const maxY = Math.max(8, window.innerHeight - 72);
    const position = {
      x: Math.min(Math.max(8, nextX), maxX),
      y: Math.min(Math.max(8, nextY), maxY)
    };

    setBadgePosition(position);
  }, [badgeDrag]);

  // Badge drag: use window-level events so drag doesn't break when cursor leaves the badge
  useEffect(() => {
    if (!badgeDrag.isDragging) return;

    const onMove = (event) => handleBadgePointerMove(event);
    const onUp = () => {
      setBadgeDrag((prev) => ({ ...prev, isDragging: false }));
      if (badgeSuppressClickRef.current) {
        // Was a real drag — persist the final position
        // Keep badgeSuppressClickRef.current = true so onClick guard catches it
        setBadgePosition((pos) => {
          if (pos) localStorage.setItem('haliGuideBadgePosition', JSON.stringify(pos));
          return pos;
        });
      } else {
        // Was a tap (no drag movement) — open the guide
        setBadgePosition(null);
        localStorage.removeItem('haliGuideBadgePosition');
        setIsOpen(true);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [badgeDrag.isDragging, handleBadgePointerMove]);

  useEffect(() => {
    if (!panelDrag.isDragging) return;

    const handleMouseMove = (event) => {
      if (!panelRef.current) return;
      const width = panelRef.current.offsetWidth;
      const height = panelRef.current.offsetHeight;
      const nextX = event.clientX - panelDrag.offsetX;
      const nextY = event.clientY - panelDrag.offsetY;
      const maxX = Math.max(8, window.innerWidth - width - 8);
      const maxY = Math.max(8, window.innerHeight - height - 8);
      const position = {
        x: Math.min(Math.max(8, nextX), maxX),
        y: Math.min(Math.max(8, nextY), maxY)
      };

      setPanelPosition(position);
      localStorage.setItem('haliGuidePanelPosition', JSON.stringify(position));
    };

    const handleMouseUp = () => {
      setPanelDrag((prev) => ({ ...prev, isDragging: false }));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [panelDrag.isDragging, panelDrag.offsetX, panelDrag.offsetY]);

  // === AI CHAT FUNCTIONS ===
  // Send message to AI backend
  const sendMessageToAI = async (text) => {
    try {
      const response = await api.post('/api/chat/chat', {
        message: text,
        conversationId: aiChatConversationId,
        userId: 'guest'
      });

      if (response.data.success) {
        setIsAIOnline(true);
        if (response.data.conversationId && !aiChatConversationId) {
          setAiChatConversationId(response.data.conversationId);
        }
        return response.data.message;
      } else {
        throw new Error('Failed to get response from AI');
      }
    } catch (error) {
      console.error('AI Chat Error:', error);
      setIsAIOnline(false);
      if (error.response?.status === 429) {
        return "I'm experiencing high demand right now. Please try again in a moment, or contact us directly at info@halisoft.biz.";
      }
      return "I'm having trouble connecting right now. Please contact our team directly:\n\n📧 Email: info@halisoft.biz\n📞 Phone: +971 58 833 9129";
    }
  };

  const handleSendAiChatMessage = async () => {
    if (!aiChatInput.trim()) return;

    const userMessage = {
      type: 'user',
      text: aiChatInput.trim(),
      timestamp: new Date()
    };
    setAiChatMessages(prev => [...prev, userMessage]);
    const messageText = aiChatInput.trim();
    setAiChatInput('');
    setAiChatIsTyping(true);

    try {
      const aiResponse = await sendMessageToAI(messageText);
      setAiChatIsTyping(false);
      const botMessage = {
        type: 'bot',
        text: aiResponse,
        timestamp: new Date()
      };
      setAiChatMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setAiChatIsTyping(false);
      const errorMessage = {
        type: 'bot',
        text: "I'm having trouble connecting right now. Please contact our team directly:\n\n📧 Email: info@halisoft.biz\n📞 Phone: +971 58 833 9129",
        timestamp: new Date()
      };
      setAiChatMessages(prev => [...prev, errorMessage]);
    }
  };


  // Scroll AI chat to bottom
  useEffect(() => {
    if (currentView === 'aiChat') {
      aiChatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [aiChatMessages, currentView]);


  // Scroll Live chat to bottom
  useEffect(() => {
    if (currentView === 'liveChat') {
      liveChatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [liveChatMessages, currentView]);

  // === LIVE CHAT FUNCTIONS ===
  // Clear invalid sessions
  useEffect(() => {
    if (liveChatSessionId && isAuthenticated && isGuestSession(liveChatSessionId)) {
      console.log('[LandingGuideAgent] Clearing guest session for authenticated user');
      setLiveChatSessionId(null);
      setLiveChatMessages([]);
      localStorage.removeItem('liveChatSessionId');
      return;
    }

    if (liveChatSessionId && isAuthenticated && user?.id && !isGuestSession(liveChatSessionId)) {
      const expectedPrefix = `chat_${user.id}_`;
      if (!liveChatSessionId.startsWith(expectedPrefix)) {
        console.log('[LandingGuideAgent] Clearing session for different authenticated user');
        setLiveChatSessionId(null);
        setLiveChatMessages([]);
        localStorage.removeItem('liveChatSessionId');
        return;
      }
    }

    if (liveChatSessionId && !isGuestSession(liveChatSessionId) && !isAuthenticated) {
      console.log('[LandingGuideAgent] Clearing invalid session for unauthenticated user');
      setLiveChatSessionId(null);
      setLiveChatMessages([]);
      localStorage.removeItem('liveChatSessionId');
    }
  }, [liveChatSessionId, isAuthenticated, user?.id]);

  // Restore live chat messages when component mounts with existing session
  useEffect(() => {
    const restoreMessages = async () => {
      if (liveChatSessionId && liveChatMessages.length === 0) {
        try {
          if (isAuthenticated && isGuestSession(liveChatSessionId)) {
            return;
          }
          console.log('[LandingGuideAgent] Restoring messages for session:', liveChatSessionId);
          const apiPath = isGuestSession(liveChatSessionId)
            ? `/api/support/chat/guest/messages/${liveChatSessionId}`
            : `/api/support/chat/messages/${liveChatSessionId}`;
          const response = await api.get(apiPath);

          if (response.data.success && response.data.messages) {
            const formattedMessages = response.data.messages.map(msg => ({
              id: msg.id,
              type: msg.sender === 'support' ? 'support' : msg.sender === 'customer' ? 'user' : 'system',
              text: msg.text,
              senderName: msg.senderName,
              timestamp: new Date(msg.timestamp),
              attachments: msg.attachments || []
            }));
            setLiveChatMessages(formattedMessages);
          }
        } catch (error) {
          console.error('[LandingGuideAgent] Error restoring messages:', error);
          // If session is invalid (403/404/500), clear it so user can start fresh
          if (error.response?.status === 403 || error.response?.status === 404 || error.response?.status === 500) {
            console.log('[LandingGuideAgent] Clearing invalid chat session due to', error.response?.status);
            setLiveChatSessionId(null);
            setLiveChatMessages([]);
            localStorage.removeItem('liveChatSessionId');
            localStorage.removeItem('liveChatLastReadMessageId');
            setUnreadChatCount(0);
          }
        }
      }
    };
    restoreMessages();
  }, [liveChatSessionId, isAuthenticated]);

  // Poll for new live chat messages
  useEffect(() => {
    if (!liveChatSessionId || currentView !== 'liveChat') return;
    if (isAuthenticated && isGuestSession(liveChatSessionId)) return;

    const pollMessages = async () => {
      try {
        const apiPath = isGuestSession(liveChatSessionId)
          ? `/api/support/chat/guest/messages/${liveChatSessionId}`
          : `/api/support/chat/messages/${liveChatSessionId}`;
        const response = await api.get(apiPath);

        if (response.data.success && response.data.messages) {
          const formattedMessages = response.data.messages.map(msg => ({
            id: msg.id,
            type: msg.sender === 'support' ? 'support' : msg.sender === 'customer' ? 'user' : 'system',
            text: msg.text,
            senderName: msg.senderName,
            timestamp: new Date(msg.timestamp),
            attachments: msg.attachments || []
          }));

          const currentIds = liveChatMessages.map(m => m.id).join(',');
          const newIds = formattedMessages.map(m => m.id).join(',');

          if (currentIds !== newIds || formattedMessages.length !== liveChatMessages.length) {
            const hasNewSupportMessage = formattedMessages.some(
              msg => msg.type === 'support' && msg.id > lastReadMessageId
            );

            if (hasNewSupportMessage) {
              setNewMessageAlert(true);
              setTimeout(() => setNewMessageAlert(false), 3000);
            }

            setLiveChatMessages(formattedMessages);
            setTimeout(() => {
              liveChatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }

          const maxMessageId = Math.max(...response.data.messages.map(m => m.id || 0));
          if (maxMessageId > lastReadMessageId) {
            setLastReadMessageId(maxMessageId);
            localStorage.setItem('liveChatLastReadMessageId', maxMessageId.toString());
            setUnreadChatCount(0);
          }
        }
      } catch (error) {
        console.error('Error polling messages:', error);
        // If session is invalid (403/404/500), clear it so user can start fresh
        if (error.response?.status === 403 || error.response?.status === 404 || error.response?.status === 500) {
          console.log('[LandingGuideAgent] Clearing invalid chat session due to', error.response?.status);
          setLiveChatSessionId(null);
          setLiveChatMessages([]);
          localStorage.removeItem('liveChatSessionId');
          localStorage.removeItem('liveChatLastReadMessageId');
          setUnreadChatCount(0);
        }
      }
    };

    const interval = setInterval(pollMessages, 5000);
    pollMessages();

    return () => clearInterval(interval);
  }, [liveChatSessionId, currentView, lastReadMessageId, isAuthenticated]);

  // Poll for unread messages count (when not in live chat view)
  useEffect(() => {
    if (!liveChatSessionId || currentView === 'liveChat') return;
    if (isAuthenticated && isGuestSession(liveChatSessionId)) return;

    const pollUnreadCount = async () => {
      try {
        const apiPath = isGuestSession(liveChatSessionId)
          ? `/api/support/chat/guest/messages/${liveChatSessionId}`
          : `/api/support/chat/messages/${liveChatSessionId}`;
        const response = await api.get(apiPath);

        if (response.data.success && response.data.messages) {
          const newSupportMessages = response.data.messages.filter(
            msg => msg.sender === 'support' && msg.id > lastReadMessageId
          );
          setUnreadChatCount(newSupportMessages.length);
        }
      } catch (error) {
        console.error('Error polling unread count:', error);
        // If session is invalid (403/404/500), clear it so user can start fresh
        if (error.response?.status === 403 || error.response?.status === 404 || error.response?.status === 500) {
          console.log('[LandingGuideAgent] Clearing invalid chat session due to', error.response?.status);
          setLiveChatSessionId(null);
          setLiveChatMessages([]);
          localStorage.removeItem('liveChatSessionId');
          localStorage.removeItem('liveChatLastReadMessageId');
          setUnreadChatCount(0);
        }
      }
    };

    const interval = setInterval(pollUnreadCount, 15000);
    pollUnreadCount();

    return () => clearInterval(interval);
  }, [liveChatSessionId, currentView, lastReadMessageId, isAuthenticated]);

  const handleOpenLiveChat = async () => {
    setCurrentView('liveChat');

    const expectedUserId = isAuthenticated && user?.id ? String(user.id) : 'guest';
    const sessionMatchesUser = liveChatSessionId && (
      (isAuthenticated && liveChatSessionId.startsWith(`chat_${expectedUserId}_`)) ||
      (!isAuthenticated && liveChatSessionId.startsWith('chat_guest_'))
    );

    if (!liveChatSessionId || !sessionMatchesUser) {
      try {
        const userName = isAuthenticated && user ?
          `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Guest User'
          : 'Guest Visitor';

        let response;
        if (isAuthenticated) {
          response = await api.post('/api/support/chat/start', {
            userId: expectedUserId,
            userName
          });
        } else {
          response = await api.post('/api/support/chat/guest/start', {
            userName
          });
        }

        if (response.data.success) {
          const sessionId = response.data.sessionId;
          setLiveChatSessionId(sessionId);
          localStorage.setItem('liveChatSessionId', sessionId);

          setLiveChatMessages([{
            type: 'system',
            text: 'Connected to HaliSoft support team. A team member will be with you shortly.',
            timestamp: new Date()
          }]);
        }
      } catch (error) {
        console.error('[LandingGuideAgent] Error starting live chat:', error);
        setLiveChatMessages([{
          type: 'system',
          text: 'Unable to connect to support team. Please try again or use alternative contact methods.',
          timestamp: new Date()
        }]);
      }
    }
  };

  const handleSendLiveChatMessage = async () => {
    if ((!liveChatInput.trim() && !selectedFile) || !liveChatSessionId) return;

    const messageText = liveChatInput.trim() || (selectedFile ? `[Attachment: ${selectedFile.name}]` : '');
    const tempId = `temp_${Date.now()}`;
    const userMessage = {
      id: tempId,
      type: 'user',
      text: messageText,
      timestamp: new Date(),
      status: 'sending',
      attachments: selectedFile ? [{
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
        uploading: true
      }] : []
    };

    setLiveChatMessages(prev => [...prev, userMessage]);
    const messageInput = liveChatInput.trim();
    const fileToSend = selectedFile;
    setLiveChatInput('');
    setSelectedFile(null);

    try {
      const useGuestApi = isGuestSession(liveChatSessionId);

      if (fileToSend && !useGuestApi) {
        const formData = new FormData();
        formData.append('sessionId', liveChatSessionId);
        formData.append('text', messageInput);
        formData.append('sender', 'customer');
        formData.append('attachment', fileToSend);

        await api.post('/api/support/chat/upload-attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setLiveChatMessages(prev => prev.map(msg =>
          msg.id === tempId ? { ...msg, status: 'sent' } : msg
        ));
      } else {
        const apiPath = useGuestApi
          ? '/api/support/chat/guest/message'
          : '/api/support/chat/message';
        await api.post(apiPath, {
          sessionId: liveChatSessionId,
          text: messageText
        });

        setLiveChatMessages(prev => prev.map(msg =>
          msg.id === tempId ? { ...msg, status: 'sent' } : msg
        ));
      }
    } catch (error) {
      console.error('[LandingGuideAgent] Error sending message:', error);
      setLiveChatMessages(prev => prev.map(msg =>
        msg.id === tempId ? { ...msg, status: 'failed' } : msg
      ));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await api.get(`/api/support/chat/download/${attachmentId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[LandingGuideAgent] Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  const handleNewLiveChatSession = () => {
    localStorage.removeItem('liveChatSessionId');
    localStorage.removeItem('liveChatLastReadMessageId');
    setLiveChatSessionId(null);
    setLiveChatMessages([]);
    setLastReadMessageId(0);
    setUnreadChatCount(0);
    handleOpenLiveChat();
  };

  // === EMAIL FUNCTIONS ===
  const handleEmailFormChange = (field, value) => {
    setEmailFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSendEmail = async () => {
    if (!emailFormData.name || !emailFormData.email || !emailFormData.message) return;

    setEmailSending(true);
    try {
      const response = await api.post('/public-api/contact/send-email', emailFormData);
      if (response.data.success) {
        setEmailSent(true);
        setEmailFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again or contact us directly at info@halisoft.biz');
    } finally {
      setEmailSending(false);
    }
  };

  // Theme colors: 'default' (blue/purple), 'haliagent' (yellow), 'haliflow' (red)
  const getThemeColors = () => {
    if (theme === 'haliagent' || theme === 'halitrade') {
      return {
        gradient: darkMode
          ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500'
          : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400',
        button: darkMode ? 'bg-yellow-600 hover:bg-yellow-500' : 'bg-yellow-500 hover:bg-yellow-400',
        headerGradient: 'bg-gradient-to-r from-yellow-500 to-orange-500',
        accentText: darkMode ? 'text-yellow-400' : 'text-yellow-600',
        inputFocus: 'focus:ring-yellow-500'
      };
    } else if (theme === 'haliflow') {
      return {
        gradient: darkMode
          ? 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500'
          : 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-400 hover:to-pink-400',
        button: darkMode ? 'bg-red-600 hover:bg-red-500' : 'bg-red-500 hover:bg-red-400',
        headerGradient: 'bg-gradient-to-r from-red-600 to-pink-600',
        accentText: darkMode ? 'text-red-400' : 'text-red-600',
        inputFocus: 'focus:ring-red-500'
      };
    } else {
      // Default (blue/purple)
      return {
        gradient: darkMode
          ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'
          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500',
        button: darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-blue-600 hover:bg-blue-500',
        headerGradient: darkMode ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'bg-gradient-to-r from-blue-600 to-purple-600',
        accentText: darkMode ? 'text-purple-400' : 'text-blue-600',
        inputFocus: 'focus:ring-purple-500'
      };
    }
  };

  const themeColors = getThemeColors();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Listen for external events to open the navigator
  useEffect(() => {
    const handleExternalOpen = () => {
      setIsOpen(true);
      setIsCentered(true); // Center the panel when opened via Get Started
      setShowQuickLinks(true); // Ensure Quick Links are visible
    };

    // Open with Contact Center visible (for landing page auto-open)
    const handleOpenWithContact = () => {
      setIsOpen(true);
      setShowQuickLinks(true); // Ensure Quick Links (including Contact Center) are visible
    };

    // Open with a specific message (e.g., "schedule a demo")
    const handleOpenWithMessage = (event) => {
      const message = event.detail?.message || 'I want to schedule a demo';
      setIsOpen(true);
      setShowQuickLinks(false);
      // Store message to be processed after render
      window.__haliGuideMessage = message;
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('platformNavigator:open', handleExternalOpen);
      window.addEventListener('platformNavigator:openWithContact', handleOpenWithContact);
      window.addEventListener('platformNavigator:openWithMessage', handleOpenWithMessage);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('platformNavigator:open', handleExternalOpen);
        window.removeEventListener('platformNavigator:openWithContact', handleOpenWithContact);
        window.removeEventListener('platformNavigator:openWithMessage', handleOpenWithMessage);
      }
    };
  }, []);

  // Process pending message when opened via platformNavigator:openWithMessage
  useEffect(() => {
    if (isOpen && window.__haliGuideMessage) {
      const message = window.__haliGuideMessage;
      delete window.__haliGuideMessage;
      // Small delay to ensure component is fully rendered
      setTimeout(() => {
        handleQuickAction(message);
      }, 200);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') { setIsOpen(false); setIsCentered(false); }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Welcome message when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addAgentMessage(
        `👋 **Welcome to HaliSoft!**\n\nWe build AI-powered software for trade finance companies. Our tools help factors, banks, and brokers process deals faster.\n\n**How can I help you today?**`,
        [
          {
            label: '💰 Get early payment for my invoices',
            onClick: () => handleQuickAction('I am a supplier looking for invoice financing to get early payment'),
            icon: ArrowRight,
            color: 'red'
          },
          {
            label: '🏢 I\'m a buyer / extend payment terms',
            onClick: () => handleQuickAction('I am a buyer and want to extend payment terms for my suppliers'),
            icon: ArrowRight,
            color: 'blue'
          },
          {
            label: '🤝 I\'m a broker / referring a client',
            onClick: () => handleQuickAction('I am a broker or partner referring a client for invoice financing'),
            icon: ArrowRight,
            color: 'purple'
          },
          {
            label: '📚 Learn about trade finance',
            onClick: () => setCurrentView('aiChat'),
            icon: ArrowRight,
            color: 'indigo'
          },
          {
            label: '💬 Talk to someone',
            onClick: () => setCurrentView('liveChat'),
            icon: ArrowRight,
            color: 'green'
          },
          {
            label: 'ℹ️ What is HaliGuide?',
            onClick: () => handleQuickAction('Explain what HaliGuide is and what it can do for me'),
            icon: ArrowRight,
            color: 'gray'
          },
          {
            label: '🏠 Go to HaliSoft Home',
            onClick: () => goToPath('/landing'),
            icon: ArrowRight,
            color: 'slate'
          },
          {
            label: isAuthenticated ? '🚪 Logout' : '🔑 Login',
            onClick: isAuthenticated ? () => goToPath('/logout') : () => goToPath('/login'),
            icon: ArrowRight,
            color: isAuthenticated ? 'orange' : 'cyan'
          }
        ]
      );
    }
  }, [isOpen, messages.length]);

  const addAgentMessage = (text, actions = null) => {
    setMessages(prev => {
      const lastMessage = prev[prev.length - 1];
      if (lastMessage && lastMessage.text === text) return prev;

      return [...prev, {
        id: `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'agent',
        text,
        actions, // Optional action buttons
        timestamp: new Date()
      }];
    });
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'user',
      text,
      timestamp: new Date()
    }]);
  };

  const handleSendMessage = async (overrideMessage = null) => {
    const rawMessage = overrideMessage != null ? overrideMessage : userInput;
    if (!rawMessage.trim() || isTyping) return;

    const userMessage = rawMessage.trim();
    addUserMessage(userMessage);
    if (overrideMessage == null) {
      setUserInput('');
    }
    setIsTyping(true);

    try {
      // Create enriched message with landing page context
      const enrichedMessage = `You are an AI guide on the HaliSoft landing page helping visitors find the right platform and next steps.

AVAILABLE PLATFORMS:
1. **HaliTrade** - Invoice financing platform for suppliers/customers
   - For businesses seeking invoice financing
   - Upload invoices and get funded
   - Landing: /halitrade-landing
   - Dashboard: /halitrade/dashboard (requires login)

2. **HaliAgent** - Agent origination platform for brokers
   - For trade finance agents, brokers, and sales teams
   - Lead management and deal sourcing
   - Landing: /haliagent-landing
   - Dashboard: /haliagent/dashboard (requires login)

3. **Platform** - Factor tools and analytics (internal/admin)
   - For financial analysts and factors
   - Risk analysis, contract review, financial tools
   - Route: /platform/dashboard (requires login)

COMMON ACTIONS:
- Login: /login
- Pricing: /pricing
- Products: /products
- Contact: /contact
- About: /about

USER QUESTION: ${userMessage}

Based on their question, help them:
1. Understand which platform is best for them
2. Guide them to the right next step (login, learn more, contact, etc.)
3. Answer their questions clearly and concisely

If appropriate, suggest they navigate to a specific page. Use phrases like:
- "You should check out HaliTrade - I can take you there"
- "It sounds like HaliAgent is perfect for you"
- "Let me direct you to our pricing page"

Be friendly, helpful, and concise. Use emojis sparingly.`;

      // Call Claude AI API
      const response = await api.post('/public-api/chat/chat', {
        message: enrichedMessage,
        conversationId: conversationId,
        context: {
          type: 'landing_guide',
          page: 'landing',
          availablePlatforms: ['haliflow', 'halitrade', 'platform']
        }
      });

      console.log('[LandingGuideAgent] API response:', response.data);

      // Store conversation ID for continuity
      if (response.data.conversationId && !conversationId) {
        setConversationId(response.data.conversationId);
      }

      // Handle different response formats
      const aiResponse = response.data.response || response.data.message || response.data.reply;

      if (aiResponse) {
        // Parse response for suggested actions
        const actions = parseActionsFromResponse(aiResponse, userMessage);
        addAgentMessage(aiResponse, actions);
      } else {
        console.error('[LandingGuideAgent] No valid response in data:', response.data);
        throw new Error('No response from AI');
      }

    } catch (error) {
      console.error('Error getting AI response:', error);

      // Fallback response
      addAgentMessage(
        `⚠️ I'm having trouble connecting right now, but I can still help!\n\n**Quick Links:**\n• [HaliTrade](${getHalitradeUrl('/halitrade-landing')}) - Invoice Financing\n• [HaliAgent](/haliagent-landing) - Agent Portal\n• [Contact Us](/contact)\n\nWhat would you like to know more about?`
      );
    } finally {
      setIsTyping(false);
    }
  };

  // Parse AI response for navigation suggestions
  const parseActionsFromResponse = (responseText, userQuestion) => {
    const actions = [];
    const lowerResponse = responseText.toLowerCase();
    const lowerQuestion = userQuestion.toLowerCase();

    // Detect platform suggestions
    if (lowerResponse.includes('haliflow') || lowerQuestion.includes('invoice') || lowerQuestion.includes('financing')) {
      actions.push({
        label: 'Go to HaliTrade',
        icon: ArrowRight,
        color: 'red', // HaliTrade = Red
        onClick: () => {
          window.location.href = getHalitradeUrl('/halitrade-landing');
          setIsOpen(false);
        }
      });
    }

    if (lowerResponse.includes('haliagent') || lowerQuestion.includes('agent')) {
      actions.push({
        label: 'Go to HaliAgent',
        icon: ArrowRight,
        color: 'yellow', // HaliAgent = Yellow
        onClick: () => goToPath('/haliagent-landing')
      });
    }

    if (lowerResponse.includes('pricing') || lowerQuestion.includes('price') || lowerQuestion.includes('cost')) {
      actions.push({
        label: 'View Pricing',
        icon: ArrowRight,
        onClick: () => {
          navigate('/halisoft-pricing');
          setIsOpen(false);
        }
      });
    }

    if (lowerResponse.includes('contact') || lowerQuestion.includes('talk') || lowerQuestion.includes('speak') || lowerQuestion.includes('support')) {
      actions.push({
        label: 'AI Chat Support',
        icon: ArrowRight,
        onClick: () => {
          window.dispatchEvent(new CustomEvent('supportChat:open'));
          setIsOpen(false);
        }
      });
      actions.push({
        label: 'Live Chat Support',
        icon: ArrowRight,
        onClick: () => {
          window.dispatchEvent(new CustomEvent('supportChat:openLiveChat'));
          setIsOpen(false);
        }
      });
    }

    return actions.length > 0 ? actions : null;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputFocus = () => {
    setShowQuickLinks(false);
  };

  const handleInputBlur = () => {
    // Optional: Re-show quick links when input loses focus
    // setShowQuickLinks(true);
  };

  const handleQuickAction = (question) => {
    // Handle specific personas with custom responses
    if (question === "I am a business partner") {
      addUserMessage(question);

      const haliagentResponse = `🤝 **HaliAgent is built for partners and brokers.**\n\nShort version: centralized deal intake, AI qualification, and a clean pipeline for trade finance opportunities.\n\n**Next step:**\n👉 [Explore HaliAgent](/haliagent-landing)`;

      addAgentMessage(haliagentResponse);
      return;
    }

    if (question === "I am looking for invoice financing") {
      addUserMessage(question);

      const halitradeLogin = getHalitradeUrl('/halitrade/login');
      const halitradeSignup = getHalitradeUrl('/halitrade/login?mode=signup');
      const halitradeResponse = `📦 **HaliTrade is for invoice financing.**\n\nShort version: upload invoices, get AI checks, and access funding decisions fast.\n\n**Next step:**\n👉 [HaliTrade Login](${halitradeLogin})\n👉 [HaliTrade Sign Up](${halitradeSignup})`;

      addAgentMessage(halitradeResponse);
      return;
    }

    if (question === "I want to test AI agents in HaliLab") {
      addUserMessage(question);

      const halilabDashboard = '/halilab/dashboard';
      const halifinAgent = '/halilab/halifin';
      const riskAgent = '/halilab/risk-analysis';
      const halilabResponse = `🧪 **HaliLab is our sandbox for AI agents.**\n\nTry a live agent now:\n👉 [HaliLab Dashboard](${halilabDashboard})\n👉 [HaliFin Agent](${halifinAgent})\n👉 [Risk Analysis Agent](${riskAgent})`;

      addAgentMessage(halilabResponse);
      return;
    }

    if (question === "I am a supplier") {
      addUserMessage(question);

      const halitradeLogin = getHalitradeUrl('/halitrade/login');
      const halitradeSignup = getHalitradeUrl('/halitrade/login?mode=signup');
      const halitradeResponse = `📦 **HaliTrade helps suppliers get paid faster.**\n\nShort version: upload invoices, verify documents, and receive funding offers.\n\n**Next step:**\n👉 [HaliTrade Login](${halitradeLogin})\n👉 [HaliTrade Sign Up](${halitradeSignup})`;

      addAgentMessage(halitradeResponse);
      return;
    }

    if (question === "I am a buyer") {
      addUserMessage(question);

      const halitradeLogin = getHalitradeUrl('/halitrade/login');
      const halitradeSignup = getHalitradeUrl('/halitrade/login?mode=signup');
      const halitradeResponse = `🛒 **HaliTrade helps buyers manage payments and suppliers.**\n\nShort version: extend terms, centralize documents, and coordinate shipments.\n\n**Next step:**\n👉 [HaliTrade Login](${halitradeLogin})\n👉 [HaliTrade Sign Up](${halitradeSignup})`;

      addAgentMessage(halitradeResponse);
      return;
    }

    if (question === "Explain what HaliGuide is and what it can do for me") {
      addUserMessage("What is HaliGuide?");

      const haliguideResponse = `🤖 **HaliGuide is your AI assistant on HaliSoft.**\n\n**What I can do:**\n• 🗣️ Answer questions about trade finance\n• 🔍 Help you find the right HaliSoft product\n• 📚 Explain how invoice factoring works\n• 💬 Connect you with our team via Live Chat\n• 📅 Help you schedule a demo\n\n**HaliSoft Products:**\n• **HaliTrade** - Invoice financing platform\n• **HaliAgent** - Partner & broker portal\n• **HaliLab** - AI tools sandbox\n\nHow can I help you today?`;

      addAgentMessage(haliguideResponse);
      return;
    }

    handleSendMessage(question);
  };

  const content = (
    <>
      {/* Bouton flottant HaliGuide - rendu dans document.body pour être toujours visible */}
      {!isOpen && isValidInstance && (
        <button
          id={BADGE_ID}
          type="button"
          aria-label="Open HaliGuide"
          data-haliguide-floating
          onClick={(e) => {
            // Guard: skip if this click follows a drag (pointer events may still fire onClick)
            if (badgeSuppressClickRef.current) {
              badgeSuppressClickRef.current = false;
              e.stopPropagation();
              return;
            }
            // Keyboard accessibility (Enter/Space) or genuine tap
            setBadgePosition(null);
            localStorage.removeItem('haliGuideBadgePosition');
            setIsOpen(true);
          }}
          className={`fixed ${badgePosition ? '' : position === 'right' ? 'bottom-20 right-3 sm:bottom-20 sm:right-4 md:bottom-24 md:right-6' : 'bottom-20 left-3 sm:bottom-20 sm:left-4 md:bottom-24 md:left-6'} z-[99999] p-2 sm:p-2 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.4)] ${badgeDrag.isDragging ? '' : 'transition-all duration-300 hover:scale-105 active:scale-95'} group border-2 ${
            theme === 'halitrade'
              ? 'bg-gradient-to-r from-yellow-500 to-orange-500 border-yellow-300'
              : theme === 'haliflow'
              ? 'bg-gradient-to-r from-red-500 to-pink-500 border-red-300'
              : 'bg-gradient-to-r from-blue-500 to-purple-600 border-blue-300'
          } ${badgeDrag.isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{
            touchAction: isMobile ? 'auto' : 'none',
            ...(badgePosition
              ? { left: `${badgePosition.x}px`, top: `${badgePosition.y}px`, right: 'auto', bottom: 'auto' }
              : {})
          }}
          onPointerDown={(event) => {
            if (isMobile) return; // On mobile, just use onClick — no drag
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            event.preventDefault();
            const rect = event.currentTarget.getBoundingClientRect();
            setBadgePosition({ x: rect.left, y: rect.top });
            badgeSuppressClickRef.current = false;
            setBadgeDrag({
              isDragging: true,
              offsetX: event.clientX - rect.left,
              offsetY: event.clientY - rect.top,
              startX: event.clientX,
              startY: event.clientY
            });
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/assets/haliguide-logo-text-dark.svg`}
            alt="HaliGuide"
            className="h-8 md:h-10"
          />
          {unreadChatCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse shadow-lg">
              {unreadChatCount > 9 ? '9+' : unreadChatCount}
            </span>
          )}
        </button>
      )}


      {/* Guide Panel - Resizable */}
      {isOpen && isValidInstance && (
        <div
          ref={panelRef}
          style={{
            width: isMobile ? '100vw' : (isCentered ? '500px' : panelWidth),
            left: isMobile ? 0 : (isCentered ? '50%' : (panelPosition ? `${panelPosition.x}px` : undefined)),
            top: isMobile ? 0 : (isCentered ? '50%' : (panelPosition ? `${panelPosition.y}px` : undefined)),
            right: isMobile ? 0 : ((isCentered || panelPosition) ? 'auto' : undefined),
            bottom: isMobile ? 0 : ((isCentered || panelPosition) ? 'auto' : undefined),
            transform: (isCentered && !isMobile) ? 'translate(-50%, -50%)' : undefined,
            height: isMobile
              ? '100dvh'
              : (isCentered ? '70vh' : (currentView === 'haliSales' ? 'calc(100vh - 120px)' : undefined)),
            minHeight: isMobile ? undefined : '360px',
            maxHeight: isMobile ? '100dvh' : (isCentered ? '70vh' : 'calc(100vh - 120px)'),
            resize: isMobile || isCentered ? 'none' : 'both'
          }}
          className={`fixed ${isMobile ? 'inset-0' : ((panelPosition && !isCentered) ? '' : 'bottom-0 left-0 right-0')} z-[99998] flex flex-col ${isMobile ? 'rounded-none' : 'rounded-t-2xl md:rounded-2xl'} shadow-[0_24px_60px_rgba(2,6,23,0.35)] overflow-hidden transition-all duration-300 ${
            isCentered
              ? ''
              : position === 'right'
                ? 'md:bottom-6 md:right-6 md:left-auto'
                : 'md:bottom-6 md:left-6 md:right-auto'
          } ${
            darkMode
              ? 'bg-[rgba(15,23,42,0.25)] backdrop-blur-[32px] border border-white/10'
              : 'bg-[rgba(255,255,255,0.25)] backdrop-blur-[32px] border border-black/10'
          }`}
        >
          {/* Resize Handle */}
          <div
            onMouseDown={startResizing}
            className={`hidden md:block absolute top-0 ${position === 'right' ? 'left-0' : 'right-0'} w-2 h-full cursor-col-resize hover:bg-blue-500/50 transition-colors group z-10 ${
              isResizing ? 'bg-blue-500/50' : ''
            }`}
            title="Drag to resize"
          >
            <div className={`absolute top-1/2 ${position === 'right' ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2'} transform -translate-y-1/2 p-1 rounded ${
              darkMode ? 'bg-gray-700' : 'bg-gray-200'
            } opacity-0 group-hover:opacity-100 transition-opacity`}>
              <GripVertical size={12} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
            </div>
          </div>
          {/* Header with Tabs */}
          <div
            className={`${themeColors.headerGradient} ${panelDrag.isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={(event) => {
              if (isMobile) return;
              if (event.button !== 0) return;
              if (event.target.closest('button')) return;
              if (!panelRef.current) return;
              const rect = panelRef.current.getBoundingClientRect();
              setPanelPosition({ x: rect.left, y: rect.top });
              setPanelDrag({
                isDragging: true,
                offsetX: event.clientX - rect.left,
                offsetY: event.clientY - rect.top
              });
            }}
          >
            <div className="px-5 py-3 flex items-center justify-between" style={isMobile ? { paddingTop: 'max(0.75rem, env(safe-area-inset-top))' } : undefined}>
              <div className="flex items-center gap-3">
                {/* HaliGuide Logo */}
                <img
                  src={`${process.env.PUBLIC_URL || ''}/assets/haliguide-logo-text-dark.svg`}
                  alt="HaliGuide"
                  className="h-8"
                />
                {/* View-specific subtitle */}
                <div className="flex items-center gap-2 text-xs text-white/80">
                  {currentView === 'aiChat' && (
                    <span className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${isAIOnline ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}></span>
                      {isAIOnline ? 'AI Online' : 'AI Offline'}
                    </span>
                  )}
                  {currentView === 'haliSales' && <span>HaliSales</span>}
                  {currentView === 'liveChat' && <span>Live Chat</span>}
                  {currentView === 'email' && <span>Email</span>}
                  {currentView === 'calendly' && <span>Demo</span>}
                </div>
              </div>
              <button
                onClick={() => { setIsOpen(false); setIsCentered(false); }}
                className="text-white/80 hover:text-white transition-colors p-2 -mr-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Tab Buttons - 5 tabs */}
            <div className="flex px-2 pb-2 gap-1 overflow-x-auto">
              <button
                onClick={() => setCurrentView('guide')}
                className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  currentView === 'guide'
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Compass className="w-3.5 h-3.5" />
                Guide
              </button>
              <button
                onClick={() => setCurrentView('aiChat')}
                className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  currentView === 'aiChat'
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Bot className="w-3.5 h-3.5" />
                AI
              </button>
              <button
                onClick={() => setCurrentView('haliSales')}
                className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  currentView === 'haliSales'
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Sales
              </button>
              <button
                onClick={() => handleOpenLiveChat()}
                className={`relative flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  currentView === 'liveChat'
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Live
                {unreadChatCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center animate-pulse" style={{ fontSize: '10px' }}>
                    {unreadChatCount > 9 ? '9+' : unreadChatCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setCurrentView('email')}
                className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  currentView === 'email'
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                Contact
              </button>
              <button
                onClick={() => setCurrentView('calendly')}
                className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  currentView === 'calendly'
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                Demo
              </button>
            </div>
          </div>

          {/* GUIDE VIEW */}
          {currentView === 'guide' && (
            <>
          {/* Quick Actions - Collapsible Scrollable Container */}
          <div className="flex-shrink-0 border-b dark:border-gray-700 border-gray-100">
            <button
              onClick={() => setShowQuickLinks(!showQuickLinks)}
              className={`w-full px-4 py-2 flex items-center justify-between backdrop-blur-md ${
                darkMode ? 'bg-gray-800/30 hover:bg-gray-800/40' : 'bg-blue-50/30 hover:bg-blue-100/40'
              } transition-colors`}
            >
              <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Quick Links
              </span>
              <ChevronDown
                size={14}
                className={`transition-transform ${showQuickLinks ? 'transform rotate-180' : ''} ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
              />
            </button>

            {showQuickLinks && (
              <div className="overflow-y-auto" style={{ maxHeight: '20vh' }}>
                <div className={`px-2 py-1.5 backdrop-blur-md ${
                  darkMode ? 'bg-gray-800/20' : 'bg-blue-50/20'
                }`}>
                  {/* Quick Questions - inline compact */}
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    <button
                      onClick={() => handleQuickAction("I am looking for invoice financing")}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600 text-gray-300'
                          : 'bg-white/80 hover:bg-red-50 text-gray-600 border border-gray-200'
                      }`}
                    >
                      Invoice Financing
                    </button>
                    <button
                      onClick={() => handleQuickAction("I am a business partner")}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600 text-gray-300'
                          : 'bg-white/80 hover:bg-blue-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      Partner
                    </button>
                    <button
                      onClick={() => handleQuickAction("I am a supplier")}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600 text-gray-300'
                          : 'bg-white/80 hover:bg-blue-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      Supplier
                    </button>
                    <button
                      onClick={() => handleQuickAction("I am a buyer")}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600 text-gray-300'
                          : 'bg-white/80 hover:bg-blue-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      Buyer
                    </button>
                    <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>|</span>
                    <button
                      onClick={() => goToPath('/landing')}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600 text-gray-300'
                          : 'bg-white/80 hover:bg-blue-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      🏠
                    </button>
                    <button
                      onClick={() => goToPath(getHalitradeUrl('/halitrade-landing'))}
                      className={`px-1 py-0.5 rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600'
                          : 'bg-white/80 hover:bg-red-50 border border-gray-200'
                      }`}
                      title="HaliTrade"
                    >
                      <img
                        src={darkMode ? "/assets/halitrade-logo-text-dark.svg" : "/assets/halitrade-logo-text.svg"}
                        alt="HaliTrade"
                        className="h-3"
                      />
                    </button>
                    <button
                      onClick={() => goToPath('/haliagent-landing')}
                      className={`px-1 py-0.5 rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600'
                          : 'bg-white/80 hover:bg-amber-50 border border-gray-200'
                      }`}
                      title="HaliAgent"
                    >
                      <img
                        src={darkMode ? "/assets/haliagent-logo-yellow-dark.svg" : "/assets/haliagent-logo-yellow.svg"}
                        alt="HaliAgent"
                        className="h-3"
                      />
                    </button>
                    <button
                      onClick={() => goToPath('/halilab/login')}
                      className={`px-1 py-0.5 rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600'
                          : 'bg-white/80 hover:bg-purple-50 border border-gray-200'
                      }`}
                      title="HaliLab"
                    >
                      <img
                        src={darkMode ? "/assets/halilab-logo-text-dark.svg" : "/assets/halilab-logo-text.svg"}
                        alt="HaliLab"
                        className="h-3"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpen(true);
                        setCurrentView('haliSales');
                      }}
                      className={`px-1.5 py-0.5 rounded transition-colors ${
                        darkMode
                          ? 'bg-gray-700/80 hover:bg-gray-600 text-amber-200'
                          : 'bg-white/80 hover:bg-amber-50 text-amber-600 border border-gray-200'
                      }`}
                      title="HaliSales"
                    >
                      <span className="text-[10px] font-semibold tracking-tight">✨ HaliSales</span>
                    </button>
                  </div>
                  {/* Pages & Sections - compact grid */}
                  <div className="flex flex-wrap gap-1">
                    <button onClick={() => { navigate('/products'); setIsOpen(false); }} className={`px-1.5 py-0.5 text-[10px] rounded ${darkMode ? 'bg-gray-700/60 hover:bg-gray-600 text-gray-400' : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500'}`}>Products</button>
                    <button onClick={() => { navigate('/halisoft-pricing'); setIsOpen(false); }} className={`px-1.5 py-0.5 text-[10px] rounded ${darkMode ? 'bg-gray-700/60 hover:bg-gray-600 text-gray-400' : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500'}`}>Pricing</button>
                    <button onClick={() => { navigate('/about'); setIsOpen(false); }} className={`px-1.5 py-0.5 text-[10px] rounded ${darkMode ? 'bg-gray-700/60 hover:bg-gray-600 text-gray-400' : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500'}`}>About</button>
                    <button onClick={() => { navigate('/landing#expertise'); setIsOpen(false); }} className={`px-1.5 py-0.5 text-[10px] rounded ${darkMode ? 'bg-gray-700/60 hover:bg-gray-600 text-gray-400' : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500'}`}>Expertise</button>
                    <button onClick={() => { navigate('/landing#platform'); setIsOpen(false); }} className={`px-1.5 py-0.5 text-[10px] rounded ${darkMode ? 'bg-gray-700/60 hover:bg-gray-600 text-gray-400' : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500'}`}>Platform</button>
                    <button onClick={() => { navigate('/faq'); setIsOpen(false); }} className={`px-1.5 py-0.5 text-[10px] rounded ${darkMode ? 'bg-gray-700/60 hover:bg-gray-600 text-gray-400' : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500'}`}>FAQ</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Messages */}
          <div className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 ${
            darkMode ? 'bg-gray-900/20' : 'bg-gray-50/20'
          }`} style={{ minHeight: '200px' }}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${message.type === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`px-4 py-3 rounded-2xl backdrop-blur-sm ${
                    message.type === 'user'
                      ? darkMode
                        ? 'bg-purple-600/90 text-white shadow-lg'
                        : 'bg-blue-600/90 text-white shadow-lg'
                      : darkMode
                        ? 'bg-gray-800/80 text-gray-200 border border-gray-700/50'
                        : 'bg-white/80 text-gray-800 border border-gray-200/50'
                  }`}>
                    {message.type === 'agent' && (
                      <div className="flex items-start gap-2 mb-1">
                        <Bot className={`w-4 h-4 mt-0.5 flex-shrink-0 ${themeColors.accentText}`} />
                        <span className={`text-xs font-semibold ${themeColors.accentText}`}>
                          AI Guide
                        </span>
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap break-words">
                      {message.type === 'agent' ? renderMessageWithLinks(message.text) : message.text}
                    </div>

                    {/* Action Buttons */}
                    {message.actions && message.actions.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {message.actions.map((action, idx) => {
                          // Determine button color
                          let buttonClass = '';
                          if (action.color === 'yellow') {
                            // HaliTrade = Yellow
                            buttonClass = darkMode
                              ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-900'
                              : 'bg-yellow-500 hover:bg-yellow-600 text-white';
                          } else if (action.color === 'red') {
                            // HaliFlow = Red
                            buttonClass = darkMode
                              ? 'bg-red-600 hover:bg-red-500 text-white'
                              : 'bg-red-600 hover:bg-red-700 text-white';
                          } else {
                            // Default colors
                            buttonClass = darkMode
                              ? 'bg-purple-600 hover:bg-purple-500 text-white'
                              : 'bg-blue-600 hover:bg-blue-500 text-white';
                          }

                          return (
                            <button
                              key={idx}
                              onClick={action.onClick}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${buttonClass}`}
                            >
                              {action.icon && <action.icon className="w-3 h-3" />}
                              {action.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className={`text-xs mt-1 ${
                    message.type === 'user' ? 'text-right text-white/60' : darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl backdrop-blur-sm ${
                  darkMode
                    ? 'bg-gray-800/80 text-gray-200 border border-gray-700/50'
                    : 'bg-white/80 text-gray-800 border border-gray-200/50'
                }`}>
                  <div className="flex items-start gap-2 mb-1">
                    <Bot className={`w-4 h-4 mt-0.5 flex-shrink-0 ${themeColors.accentText}`} />
                    <span className={`text-xs font-semibold ${themeColors.accentText}`}>
                      AI Guide
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`flex-shrink-0 px-4 py-3 border-t backdrop-blur-md ${
            darkMode ? 'bg-gray-800/30 border-gray-700/30' : 'bg-white/30 border-gray-200/30'
          }`} style={isMobile ? { paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' } : undefined}>
            <div className="flex gap-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={isTyping}
                placeholder={isTyping ? "AI is thinking..." : "Ask me anything..."}
                className={`flex-1 px-4 py-2 rounded-xl text-sm focus:ring-2 ${themeColors.inputFocus} focus:outline-none ${
                  darkMode
                    ? 'bg-gray-700 text-white placeholder-gray-400 border border-gray-600'
                    : 'bg-gray-100 text-gray-900 placeholder-gray-500 border border-gray-300'
                }`}
              />
              <button
                onClick={handleSendMessage}
                disabled={!userInput.trim() || isTyping}
                className={`px-4 py-2 rounded-xl transition-all duration-200 ${
                  userInput.trim() && !isTyping
                    ? `${themeColors.button} text-white`
                    : darkMode
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
            </>
          )}

          {/* AI CHAT VIEW */}
          {currentView === 'aiChat' && (
            <>
              {/* AI Chat Messages */}
              <div className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 ${
                darkMode ? 'bg-gray-900/20' : 'bg-gray-50/20'
              }`} style={{ minHeight: '200px' }}>
                {aiChatMessages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] ${message.type === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`px-4 py-3 rounded-2xl backdrop-blur-sm ${
                        message.type === 'user'
                          ? darkMode
                            ? 'bg-purple-600/90 text-white shadow-lg'
                            : 'bg-blue-600/90 text-white shadow-lg'
                          : darkMode
                            ? 'bg-gray-800/80 text-gray-200 border border-gray-700/50'
                            : 'bg-white/80 text-gray-800 border border-gray-200/50'
                      }`}>
                        {message.type === 'bot' && (
                          <div className="flex items-start gap-2 mb-1">
                            <Bot className={`w-4 h-4 mt-0.5 flex-shrink-0 ${themeColors.accentText}`} />
                            <span className={`text-xs font-semibold ${themeColors.accentText}`}>
                              AI Support
                            </span>
                          </div>
                        )}
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {message.type === 'bot' ? renderMessageWithLinks(message.text) : message.text}
                        </div>
                      </div>
                      <div className={`text-xs mt-1 ${
                        message.type === 'user' ? 'text-right' : ''
                      } ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {message.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}

                {aiChatIsTyping && (
                  <div className="flex justify-start">
                    <div className={`px-4 py-3 rounded-2xl backdrop-blur-sm ${
                      darkMode
                        ? 'bg-gray-800/80 text-gray-200 border border-gray-700/50'
                        : 'bg-white/80 text-gray-800 border border-gray-200/50'
                    }`}>
                      <div className="flex items-start gap-2 mb-1">
                        <Bot className={`w-4 h-4 mt-0.5 flex-shrink-0 ${themeColors.accentText}`} />
                        <span className={`text-xs font-semibold ${themeColors.accentText}`}>
                          AI Support
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={aiChatMessagesEndRef} />
              </div>

              {/* Quick Replies */}
              <div className={`px-4 py-2 border-t ${darkMode ? 'border-gray-700/30 bg-gray-800/20' : 'border-gray-200/30 bg-gray-50/20'}`}>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: '🚀 Products', value: 'Tell me about your products' },
                    { label: '💰 Pricing', value: 'How much does factoring cost?' },
                    { label: '📋 Get Started', value: 'How do I get started?' }
                  ].map((reply, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setAiChatInput(reply.value);
                        setTimeout(() => handleSendAiChatMessage(), 100);
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {reply.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Chat Input */}
              <div className={`flex-shrink-0 px-4 py-3 border-t backdrop-blur-md ${
                darkMode ? 'bg-gray-800/30 border-gray-700/30' : 'bg-white/30 border-gray-200/30'
              }`}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={aiChatInput}
                    onChange={(e) => setAiChatInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendAiChatMessage();
                      }
                    }}
                    disabled={aiChatIsTyping}
                    placeholder={aiChatIsTyping ? "AI is thinking..." : "Ask AI anything..."}
                    className={`flex-1 px-4 py-2 rounded-xl text-sm focus:ring-2 ${themeColors.inputFocus} focus:outline-none ${
                      darkMode
                        ? 'bg-gray-700 text-white placeholder-gray-400 border border-gray-600'
                        : 'bg-gray-100 text-gray-900 placeholder-gray-500 border border-gray-300'
                    }`}
                  />
                  <button
                    onClick={handleSendAiChatMessage}
                    disabled={!aiChatInput.trim() || aiChatIsTyping}
                    className={`px-4 py-2 rounded-xl transition-all duration-200 ${
                      aiChatInput.trim() && !aiChatIsTyping
                        ? `${themeColors.button} text-white`
                        : darkMode
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* HALISALES VIEW */}
          {currentView === 'haliSales' && (
            <>
              <div className="flex-1 min-h-0 h-full overflow-hidden">
                <HaliLeadsAgent embedded initiallyOpen user={user} />
              </div>
            </>
          )}

          {/* LIVE CHAT VIEW */}
          {currentView === 'liveChat' && (
            <>
              {/* Live Chat Header */}
              <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} ${newMessageAlert ? 'bg-green-500/20 animate-pulse' : ''}`}>
                <div className="flex items-center gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>Chat with Support Team</h3>
                      {newMessageAlert && (
                        <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full animate-bounce">
                          New message!
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {liveChatSessionId ? `Session: ${liveChatSessionId.slice(-8)}` : 'Connecting...'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentView('guide')}
                    className={`px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-xs flex items-center gap-1`}
                    title="Back to Guide"
                  >
                    <ArrowLeft size={14} />
                    Back
                  </button>
                  <button
                    onClick={handleNewLiveChatSession}
                    className={`px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-xs`}
                    title="Start new session"
                  >
                    New
                  </button>
                </div>
              </div>

              {/* Live Chat Messages */}
              <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${darkMode ? 'bg-gray-900/20' : 'bg-gray-50/20'}`}>
                {liveChatMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.type === 'user'
                          ? darkMode ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'
                          : msg.type === 'system'
                          ? darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-900'
                          : darkMode
                          ? 'bg-gray-800 text-gray-100'
                          : 'bg-white text-gray-800 border border-gray-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-line">{msg.text}</p>
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {msg.attachments.map((attachment, attIndex) => (
                            <div
                              key={attIndex}
                              className={`flex items-center gap-2 p-2 rounded ${
                                msg.type === 'user'
                                  ? 'bg-white/20'
                                  : darkMode
                                  ? 'bg-gray-700'
                                  : 'bg-gray-100'
                              }`}
                            >
                              <FileText size={16} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs truncate">{attachment.fileName}</p>
                                <p className="text-xs opacity-70">
                                  {(attachment.fileSize / 1024).toFixed(1)} KB
                                </p>
                              </div>
                              {!attachment.uploading && attachment.id && (
                                <button
                                  onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
                                  className="p-1 rounded hover:bg-white/20 transition-colors"
                                  title="Download"
                                >
                                  <Download size={14} />
                                </button>
                              )}
                              {attachment.uploading && (
                                <span className="text-xs opacity-70">Uploading...</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className="text-xs opacity-70">
                          {msg.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {msg.type === 'user' && (
                          <span className="ml-1 flex items-center">
                            {msg.status === 'sending' && (
                              <span className="w-3 h-3 border border-white/50 border-t-transparent rounded-full animate-spin" />
                            )}
                            {msg.status === 'sent' && <Check size={14} className="text-white/70" />}
                            {msg.status === 'delivered' && <CheckCheck size={14} className="text-white/70" />}
                            {msg.status === 'read' && <CheckCheck size={14} className="text-blue-300" />}
                            {msg.status === 'failed' && <span className="text-red-300 text-xs">!</span>}
                            {!msg.status && msg.id && !String(msg.id).startsWith('temp_') && (
                              <CheckCheck size={14} className="text-white/70" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={liveChatMessagesEndRef} />
              </div>

              {/* Live Chat Input */}
              <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white/30'}`}>
                {selectedFile && (
                  <div className={`mb-2 p-2 rounded flex items-center justify-between ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="flex items-center gap-2">
                      <FileText size={16} />
                      <div>
                        <p className="text-xs font-medium">{selectedFile.name}</p>
                        <p className="text-xs opacity-70">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveFile}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`p-2 rounded-lg transition-colors ${
                      darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                    aria-label="Attach file"
                    title="Attach file (max 10MB)"
                  >
                    <Paperclip size={20} />
                  </button>
                  <input
                    type="text"
                    value={liveChatInput}
                    onChange={(e) => setLiveChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendLiveChatMessage()}
                    placeholder="Type your message..."
                    className={`flex-1 px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 ${themeColors.inputFocus}`}
                  />
                  <button
                    onClick={handleSendLiveChatMessage}
                    className={`p-2 rounded-lg text-white transition-colors ${themeColors.button}`}
                    aria-label="Send message"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* EMAIL VIEW */}
          {currentView === 'email' && (
            <>
              <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>Send us an Email</h3>
                <button
                  onClick={() => setCurrentView('guide')}
                  className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  <X size={20} />
                </button>
              </div>
              <div className={`flex-1 overflow-y-auto p-4 ${darkMode ? 'bg-gray-900/20' : 'bg-gray-50/20'}`}>
                {emailSent ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className={`w-16 h-16 rounded-full ${darkMode ? 'bg-green-900' : 'bg-green-100'} flex items-center justify-center mb-4`}>
                      <Mail size={32} className={darkMode ? 'text-green-400' : 'text-green-600'} />
                    </div>
                    <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Email Sent Successfully!
                    </h3>
                    <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      We'll get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => { setEmailSent(false); setCurrentView('guide'); }}
                      className={`px-4 py-2 rounded-lg text-white transition-colors ${themeColors.button}`}
                    >
                      Back to Guide
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={emailFormData.name}
                        onChange={(e) => handleEmailFormChange('name', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          darkMode
                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        } focus:outline-none focus:ring-2 ${themeColors.inputFocus}`}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={emailFormData.email}
                        onChange={(e) => handleEmailFormChange('email', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          darkMode
                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        } focus:outline-none focus:ring-2 ${themeColors.inputFocus}`}
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Subject
                      </label>
                      <input
                        type="text"
                        value={emailFormData.subject}
                        onChange={(e) => handleEmailFormChange('subject', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          darkMode
                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        } focus:outline-none focus:ring-2 ${themeColors.inputFocus}`}
                        placeholder="What is this about?"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={emailFormData.message}
                        onChange={(e) => handleEmailFormChange('message', e.target.value)}
                        rows={5}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          darkMode
                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        } focus:outline-none focus:ring-2 ${themeColors.inputFocus} resize-none`}
                        placeholder="How can we help you?"
                      />
                    </div>
                    <button
                      onClick={handleSendEmail}
                      disabled={emailSending || !emailFormData.name || !emailFormData.email || !emailFormData.message}
                      className={`w-full py-2.5 rounded-lg text-white font-medium transition-colors ${
                        emailSending || !emailFormData.name || !emailFormData.email || !emailFormData.message
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:opacity-90'
                      } ${themeColors.button}`}
                    >
                      {emailSending ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* CALENDLY VIEW */}
          {currentView === 'calendly' && (
            <>
              <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>Schedule a Call</h3>
                <button
                  onClick={() => setCurrentView('guide')}
                  className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1">
                <iframe
                  src="https://calendly.com/loiclamy/30min"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Schedule Meeting"
                  style={{ minHeight: '500px' }}
                ></iframe>
              </div>
            </>
          )}

        </div>
      )}
    </>
  );

  // Don't render if this is not a valid instance (duplicate)
  if (!isValidInstance) {
    return null;
  }

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(content, document.body);
  }
  return content;
};

// Error boundary to prevent silent crashes from hiding the badge
class LandingGuideAgentBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.error('[LandingGuideAgent] Render error caught by boundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      // Fallback: render a minimal clickable badge so users can still reach support
      return createPortal(
        <button
          id="haliguide-floating-badge"
          type="button"
          aria-label="Open HaliGuide"
          onClick={() => window.location.assign('/landing#contact')}
          className="fixed bottom-6 right-3 sm:bottom-20 sm:right-4 md:bottom-24 md:right-6 z-[99999] p-3 sm:p-2 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.4)] bg-gradient-to-r from-blue-500 to-purple-600 border-2 border-blue-300 cursor-pointer"
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/assets/haliguide-logo-text-dark.svg`}
            alt="HaliGuide"
            className="h-8 md:h-10"
          />
        </button>,
        document.body
      );
    }
    return this.props.children;
  }
}

const LandingGuideAgentWithBoundary = (props) => (
  <LandingGuideAgentBoundary>
    <LandingGuideAgent {...props} />
  </LandingGuideAgentBoundary>
);

export default LandingGuideAgentWithBoundary;
